/**
 * Volume Discount — product-page embedded quantity breaks / Buy X Get Y.
 * Local seed: exports/volume-discount-template.json
 */
import { POPUP_LAYOUT_IDS, isPopupLayout } from "./popup-layouts/index.js";

export const VOLUME_DISCOUNT_TEMPLATE_TYPE = "Volume Discount";
export const VOLUME_DISCOUNT_LAYOUT_ID = POPUP_LAYOUT_IDS.VOLUME_DISCOUNT;
export const VOLUME_DISCOUNT_SLOT_NO = "5";

/** @readonly */
export const VOLUME_DISCOUNT_OFFER_MODE = Object.freeze({
  QUANTITY_BREAKS: "quantity_breaks",
  BUY_X_GET_Y: "buy_x_get_y",
});

/** @readonly */
export const VOLUME_DISCOUNT_DISCOUNT_TYPE = Object.freeze({
  NONE: "none",
  PERCENTAGE: "percentage",
  FIXED_AMOUNT: "fixed_amount",
});

/** @readonly */
export const VOLUME_DISCOUNT_OFFERS_LAYOUT = Object.freeze({
  VERTICAL: "vertical",
  HORIZONTAL: "horizontal",
});

export const VOLUME_DISCOUNT_ITEMS_PER_ROW_MIN = 2;
export const VOLUME_DISCOUNT_ITEMS_PER_ROW_MAX = 5;
export const VOLUME_DISCOUNT_ITEMS_PER_ROW_DEFAULT = 3;

export const VOLUME_DISCOUNT_OFFER_MODE_OPTIONS = Object.freeze([
  {
    value: VOLUME_DISCOUNT_OFFER_MODE.QUANTITY_BREAKS,
    label: "Quantity breaks",
    description: "Buy more of the same product to unlock bigger savings",
  },
  {
    value: VOLUME_DISCOUNT_OFFER_MODE.BUY_X_GET_Y,
    label: "Buy X get Y",
    description: "Buy items and get others free or discounted",
  },
]);

export const VOLUME_DISCOUNT_OFFERS_LAYOUT_OPTIONS = Object.freeze([
  {
    value: VOLUME_DISCOUNT_OFFERS_LAYOUT.VERTICAL,
    label: "Vertical list",
    description: "Stacked offer rows with radio selection",
    preview: "vd-vertical-list",
  },
  {
    value: VOLUME_DISCOUNT_OFFERS_LAYOUT.HORIZONTAL,
    label: "Horizontal cards",
    description: "Side-by-side offer cards",
    preview: "vd-horizontal-cards",
  },
]);

export const VOLUME_DISCOUNT_DISCOUNT_TYPE_OPTIONS = Object.freeze([
  { label: "No discount", value: VOLUME_DISCOUNT_DISCOUNT_TYPE.NONE },
  { label: "Percentage", value: VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE },
  { label: "Fixed amount", value: VOLUME_DISCOUNT_DISCOUNT_TYPE.FIXED_AMOUNT },
]);

export const VOLUME_DISCOUNT_TABS = Object.freeze([
  { id: "settings", label: "Settings" },
  { id: "offer", label: "Offer" },
  { id: "design", label: "Design" },
]);

/** Part tags on typed sections (same pattern as productPagePart / cartDrawerPart). */
export const VOLUME_DISCOUNT_PART = Object.freeze({
  HEADER_TITLE: "header_title",
  HEADER_BADGE: "header_badge",
  HEADER_SUBTITLE: "header_subtitle",
  COUNTDOWN: "countdown",
  OFFERS: "offers",
  PROMO_BANNER: "promo_banner",
  CTA: "cta",
});

export const VOLUME_DISCOUNT_SECTION_TYPES = Object.freeze({
  TITLE: "TITLE",
  SUBTITLE: "SUBTITLE",
  BADGE: "BADGE_1",
  COUNTDOWN: "COUNTDOWN",
  OFFER_PRODUCT: "OFFER_PRODUCT",
  TEXT: "TEXT",
  BUTTON: "BUTTON",
});

function defaultCss(overrides = {}) {
  return {
    desktop: {
      fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
      fontSize: "14px",
      fontWeight: "600",
      color: "#111827",
      ...overrides.desktop,
    },
    mobile: {
      fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
      fontSize: "13px",
      fontWeight: "600",
      color: "#111827",
      ...overrides.mobile,
    },
  };
}

/**
 * Build typed sections from layout blob (mirrors Product Page Upsell / Custom Cart).
 * Offers live on OFFER_PRODUCT.offerProduct; copy/CTA on TITLE/BUTTON/etc.
 */
export function buildVolumeDiscountSections(layout) {
  const L = layout || buildDefaultVolumeDiscountLayout();
  const countdownEnabled = Boolean(L.countdown?.enabled);
  const promoEnabled = L.promoBanner?.enabled !== false;
  const showTitle = L.showTitle !== false;
  const showBadge = L.showBadge !== false && Boolean(L.badge);
  const showSubtitle = L.showSubtitle !== false && Boolean(L.subtitle);

  return [
    {
      type: VOLUME_DISCOUNT_SECTION_TYPES.TITLE,
      text: L.title || "Buy More Save More",
      setting: {
        volumeDiscountPart: VOLUME_DISCOUNT_PART.HEADER_TITLE,
        visibility: { visible: showTitle },
        customCSS: defaultCss({
          desktop: { fontSize: "16px", fontWeight: "700" },
          mobile: { fontSize: "15px", fontWeight: "700" },
        }),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: VOLUME_DISCOUNT_SECTION_TYPES.BADGE,
      text: L.badge || "",
      setting: {
        volumeDiscountPart: VOLUME_DISCOUNT_PART.HEADER_BADGE,
        visibility: { visible: showBadge },
        customCSS: defaultCss({
          desktop: { fontSize: "11px", fontWeight: "600", color: "#1B7A3D" },
          mobile: { fontSize: "11px", fontWeight: "600", color: "#1B7A3D" },
        }),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: VOLUME_DISCOUNT_SECTION_TYPES.SUBTITLE,
      text: L.subtitle || "",
      setting: {
        volumeDiscountPart: VOLUME_DISCOUNT_PART.HEADER_SUBTITLE,
        visibility: { visible: showSubtitle },
        customCSS: defaultCss({
          desktop: { fontSize: "13px", fontWeight: "400", color: "#6b7280" },
          mobile: { fontSize: "12px", fontWeight: "400", color: "#6b7280" },
        }),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: VOLUME_DISCOUNT_SECTION_TYPES.COUNTDOWN,
      text: L.countdown?.title || "Hurry up! Offer expires in {{timer}}",
      setting: {
        volumeDiscountPart: VOLUME_DISCOUNT_PART.COUNTDOWN,
        visibility: { visible: countdownEnabled },
        countdown: {
          type: L.countdown?.type || "fixed",
          durationMinutes: Number(L.countdown?.durationMinutes) || 60,
        },
        customCSS: defaultCss({
          desktop: { fontSize: "13px", fontWeight: "600", color: "#f9a8d4" },
          mobile: { fontSize: "12px", fontWeight: "600", color: "#f9a8d4" },
        }),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: VOLUME_DISCOUNT_SECTION_TYPES.OFFER_PRODUCT,
      /** Tier offers — same shape as volumeDiscountLayout.offers */
      offerProduct: Array.isArray(L.offers) ? L.offers : [],
      setting: {
        volumeDiscountPart: VOLUME_DISCOUNT_PART.OFFERS,
        offerMode: normalizeVolumeDiscountOfferMode(L.offerMode),
        defaultSelectedIndex: Number(L.defaultSelectedIndex) || 0,
        showVariantSelectors: L.showVariantSelectors !== false,
        offersLayout: normalizeVolumeDiscountOffersLayout(L.offersLayout),
        showCurrentPageProduct: L.showCurrentPageProduct === true,
        itemsPerRow: clampVolumeDiscountItemsPerRow(L.itemsPerRow),
        customCSS: { desktop: {}, mobile: {} },
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: VOLUME_DISCOUNT_SECTION_TYPES.TEXT,
      text: L.promoBanner?.text || "Special offer — unlock bigger savings on this pack",
      setting: {
        volumeDiscountPart: VOLUME_DISCOUNT_PART.PROMO_BANNER,
        visibility: { visible: promoEnabled },
        customCSS: defaultCss({
          desktop: { fontSize: "13px", fontWeight: "500", color: "#1B7A3D" },
          mobile: { fontSize: "12px", fontWeight: "500", color: "#1B7A3D" },
        }),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
  ];
}

function findVolumeDiscountSection(sections, part) {
  if (!Array.isArray(sections)) return null;
  return (
    sections.find((s) => s?.setting?.volumeDiscountPart === part) || null
  );
}

/**
 * Prefer layout blob; hydrate missing fields from typed sections when present.
 */
export function resolveVolumeDiscountLayoutFromPopup(popup) {
  const base =
    getVolumeDiscountLayout(popup) ||
    buildDefaultVolumeDiscountLayout(VOLUME_DISCOUNT_OFFER_MODE.QUANTITY_BREAKS);
  const step = popup?.schema?.[0];
  const sections = step?.section || step?.sections || [];
  if (!sections.length) return base;

  const title = findVolumeDiscountSection(sections, VOLUME_DISCOUNT_PART.HEADER_TITLE);
  const badge = findVolumeDiscountSection(sections, VOLUME_DISCOUNT_PART.HEADER_BADGE);
  const subtitle = findVolumeDiscountSection(
    sections,
    VOLUME_DISCOUNT_PART.HEADER_SUBTITLE
  );
  const countdown = findVolumeDiscountSection(
    sections,
    VOLUME_DISCOUNT_PART.COUNTDOWN
  );
  const offersSec = findVolumeDiscountSection(sections, VOLUME_DISCOUNT_PART.OFFERS);
  const promo = findVolumeDiscountSection(sections, VOLUME_DISCOUNT_PART.PROMO_BANNER);

  return {
    ...base,
    title: title?.text ?? base.title,
    badge: badge?.text ?? base.badge,
    subtitle: subtitle?.text ?? base.subtitle,
    showTitle: base.showTitle !== false,
    showBadge: base.showBadge !== false,
    showSubtitle: base.showSubtitle !== false,
    /** Widget ATC removed — always use theme Add to Cart. */
    showCta: false,
    offerMode: normalizeVolumeDiscountOfferMode(
      offersSec?.setting?.offerMode || base.offerMode
    ),
    defaultSelectedIndex:
      offersSec?.setting?.defaultSelectedIndex ?? base.defaultSelectedIndex,
    showVariantSelectors:
      offersSec?.setting?.showVariantSelectors ?? base.showVariantSelectors,
    offersLayout: normalizeVolumeDiscountOffersLayout(
      offersSec?.setting?.offersLayout ?? base.offersLayout
    ),
    showCurrentPageProduct:
      (offersSec?.setting?.showCurrentPageProduct ??
        base.showCurrentPageProduct) === true,
    itemsPerRow: clampVolumeDiscountItemsPerRow(
      offersSec?.setting?.itemsPerRow ?? base.itemsPerRow
    ),
    offers: Array.isArray(offersSec?.offerProduct)
      ? offersSec.offerProduct
      : base.offers,
    promoBanner: {
      ...(base.promoBanner || {}),
      text: promo?.text ?? base.promoBanner?.text,
      enabled:
        promo?.setting?.visibility?.visible !== false &&
        base.promoBanner?.enabled !== false,
    },
    countdown: {
      ...(base.countdown || {}),
      title: countdown?.text ?? base.countdown?.title,
      enabled:
        countdown?.setting?.visibility?.visible === true ||
        Boolean(base.countdown?.enabled),
      type: countdown?.setting?.countdown?.type || base.countdown?.type,
      durationMinutes:
        countdown?.setting?.countdown?.durationMinutes ??
        base.countdown?.durationMinutes,
    },
  };
}

function uid(prefix = "offer") {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

export function isStaticVolumeDiscount(popup) {
  if (!popup) return false;
  if (popup?.generalSettings?.isStaticVolumeDiscount === true) return true;
  if (isPopupLayout(popup, POPUP_LAYOUT_IDS.VOLUME_DISCOUNT)) return true;
  const type = String(popup?.popUpType || popup?.template_type || "").trim();
  return type === VOLUME_DISCOUNT_TEMPLATE_TYPE;
}

export function normalizeVolumeDiscountOfferMode(value) {
  if (value === VOLUME_DISCOUNT_OFFER_MODE.BUY_X_GET_Y) {
    return VOLUME_DISCOUNT_OFFER_MODE.BUY_X_GET_Y;
  }
  return VOLUME_DISCOUNT_OFFER_MODE.QUANTITY_BREAKS;
}

export function normalizeVolumeDiscountOffersLayout(value) {
  if (value === VOLUME_DISCOUNT_OFFERS_LAYOUT.HORIZONTAL) {
    return VOLUME_DISCOUNT_OFFERS_LAYOUT.HORIZONTAL;
  }
  return VOLUME_DISCOUNT_OFFERS_LAYOUT.VERTICAL;
}

export function clampVolumeDiscountItemsPerRow(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    const fb = Number(fallback);
    return Number.isFinite(fb)
      ? Math.min(
          VOLUME_DISCOUNT_ITEMS_PER_ROW_MAX,
          Math.max(VOLUME_DISCOUNT_ITEMS_PER_ROW_MIN, Math.round(fb))
        )
      : VOLUME_DISCOUNT_ITEMS_PER_ROW_DEFAULT;
  }
  return Math.min(
    VOLUME_DISCOUNT_ITEMS_PER_ROW_MAX,
    Math.max(VOLUME_DISCOUNT_ITEMS_PER_ROW_MIN, Math.round(n))
  );
}

export function normalizeVolumeDiscountDiscountType(value) {
  if (value === VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE) {
    return VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE;
  }
  if (value === VOLUME_DISCOUNT_DISCOUNT_TYPE.FIXED_AMOUNT) {
    return VOLUME_DISCOUNT_DISCOUNT_TYPE.FIXED_AMOUNT;
  }
  return VOLUME_DISCOUNT_DISCOUNT_TYPE.NONE;
}

export function createQuantityBreakOffer(overrides = {}) {
  return {
    id: uid("qb"),
    quantity: 1,
    discountType: VOLUME_DISCOUNT_DISCOUNT_TYPE.NONE,
    discountValue: 0,
    discountLabel: "{saved_percentage}% off",
    title: "Buy 1",
    subtitle: "Standard Price",
    badge: "",
    ...overrides,
  };
}

export function createBuyXGetYOffer(overrides = {}) {
  return {
    id: uid("bxgy"),
    buyQuantity: 1,
    buyDiscountType: VOLUME_DISCOUNT_DISCOUNT_TYPE.NONE,
    buyDiscountValue: 0,
    getQuantity: 1,
    getDiscountType: VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE,
    getDiscountValue: 100,
    discountLabel: "{saved_percentage}% off",
    title: "Buy 1, get 1 free",
    subtitle: "",
    badge: "",
    ...overrides,
  };
}

export function defaultQuantityBreakOffers() {
  return [
    createQuantityBreakOffer({
      id: "qb_1",
      quantity: 1,
      discountType: VOLUME_DISCOUNT_DISCOUNT_TYPE.NONE,
      discountValue: 0,
      title: "Buy 1",
      subtitle: "Standard Price",
      badge: "",
      discountLabel: "",
    }),
    createQuantityBreakOffer({
      id: "qb_2",
      quantity: 2,
      discountType: VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE,
      discountValue: 15,
      title: "Buy 2",
      subtitle: "Most Popular",
      badge: "Most popular",
      discountLabel: "{saved_percentage}% off",
    }),
    createQuantityBreakOffer({
      id: "qb_3",
      quantity: 3,
      discountType: VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE,
      discountValue: 25,
      title: "Buy 3",
      subtitle: "Best Value",
      badge: "",
      discountLabel: "{saved_percentage}% off",
    }),
  ];
}

export function defaultBuyXGetYOffers() {
  return [
    createBuyXGetYOffer({
      id: "bxgy_1",
      buyQuantity: 1,
      getQuantity: 1,
      getDiscountType: VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE,
      getDiscountValue: 100,
      title: "Buy 1, get 1 free",
      discountLabel: "{saved_percentage}% off",
    }),
    createBuyXGetYOffer({
      id: "bxgy_2",
      buyQuantity: 2,
      getQuantity: 3,
      getDiscountType: VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE,
      getDiscountValue: 100,
      title: "Buy 2, get 3 free",
      badge: "Most popular",
      discountLabel: "{saved_percentage}% off",
    }),
    createBuyXGetYOffer({
      id: "bxgy_3",
      buyQuantity: 1,
      getQuantity: 5,
      getDiscountType: VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE,
      getDiscountValue: 100,
      title: "Buy 1, get 5 free",
      discountLabel: "{saved_percentage}% off",
    }),
  ];
}

export function buildDefaultVolumeDiscountLayout(offerMode) {
  const mode = normalizeVolumeDiscountOfferMode(offerMode);
  const isBxgy = mode === VOLUME_DISCOUNT_OFFER_MODE.BUY_X_GET_Y;
  return {
    offerMode: mode,
    title: isBxgy ? "Bundle & save" : "Buy More Save More",
    badge: isBxgy ? "" : "High Converting",
    subtitle: isBxgy ? "" : "The more you buy, the more you save",
    showTitle: true,
    showBadge: !isBxgy,
    showSubtitle: !isBxgy,
    discountName: "Discount",
    promoBanner: {
      enabled: !isBxgy,
      text: "Special offer — unlock bigger savings on this pack",
    },
    countdown: {
      enabled: false,
      type: "fixed",
      durationMinutes: 60,
      title: "Hurry up! Offer expires in {{timer}}",
    },
    showCta: false,
    defaultSelectedIndex: isBxgy ? 0 : 1,
    showVariantSelectors: true,
    offersLayout: VOLUME_DISCOUNT_OFFERS_LAYOUT.VERTICAL,
    showCurrentPageProduct: false,
    itemsPerRow: VOLUME_DISCOUNT_ITEMS_PER_ROW_DEFAULT,
    offers: isBxgy ? defaultBuyXGetYOffers() : defaultQuantityBreakOffers(),
    design: {
      fontFamily: "theme-default",
      // Header
      titleSize: 16,
      titleColor: "#111827",
      subtitleSize: 13,
      subtitleColor: "#6b7280",
      headerBadgeSize: 11,
      headerBadgeBackground: "#E8F8EE",
      headerBadgeTextColor: "#1B7A3D",
      // Widget shell
      shellBackground: "#ffffff",
      shellBorderWidth: 1,
      shellBorderColor: "#e5e7eb",
      cardBorderRadius: 12,
      shellPadding: 16,
      // Offer cards
      offerBackground: "#ffffff",
      offerBorderWidth: 1,
      offerBorderColor: "#e5e7eb",
      offerBorderRadius: 10,
      accentColor: "#7C5CFF",
      selectedBorderColor: "#7C5CFF",
      selectedBackground: "#F5F0FF",
      offerTitleSize: 14,
      offerTitleColor: "#111827",
      offerSubtitleSize: 12,
      offerSubtitleColor: "#6b7280",
      priceSize: 15,
      priceColor: "#111827",
      compareSize: 12,
      compareColor: "#9ca3af",
      // Save / corner badges
      badgeBackground: "#EDE7FF",
      badgeTextColor: "#5B3FD4",
      badgeSize: 11,
      cornerBadgeBackground: "#111111",
      cornerBadgeTextColor: "#FFFFFF",
      cornerBadgeSize: 10,
      // Promo
      promoBackground: "#E8F8EE",
      promoTextColor: "#1B7A3D",
      promoSize: 13,
      promoBorderRadius: 8,
      // Countdown
      countdownBackground: "#0f2744",
      countdownTextColor: "#f9a8d4",
      countdownSize: 13,
      // Variant selectors
      variantSize: 12,
      variantColor: "#4b5563",
      variantBackground: "#ffffff",
      variantBorderWidth: 1,
      variantBorderColor: "#e5e7eb",
      variantBorderRadius: 8,
      headerBadge: "High Converting",
    },
  };
}

/**
 * Resolve total units for an offer (for ATC qty / variant rows).
 */
export function getVolumeDiscountOfferUnitCount(offer, offerMode) {
  const mode = normalizeVolumeDiscountOfferMode(offerMode);
  if (mode === VOLUME_DISCOUNT_OFFER_MODE.BUY_X_GET_Y) {
    const buy = Math.max(0, Number(offer?.buyQuantity) || 0);
    const get = Math.max(0, Number(offer?.getQuantity) || 0);
    return buy + get;
  }
  return Math.max(1, Number(offer?.quantity) || 1);
}

/**
 * Price math for one offer against a unit price (store currency units).
 * @returns {{ original: number, final: number, saved: number, savedPercentage: number }}
 */
export function calculateVolumeDiscountOfferPrice(offer, unitPrice, offerMode) {
  const price = Math.max(0, Number(unitPrice) || 0);
  const mode = normalizeVolumeDiscountOfferMode(offerMode);
  let original = 0;
  let final = 0;

  if (mode === VOLUME_DISCOUNT_OFFER_MODE.BUY_X_GET_Y) {
    const buyQty = Math.max(0, Number(offer?.buyQuantity) || 0);
    const getQty = Math.max(0, Number(offer?.getQuantity) || 0);
    original = price * (buyQty + getQty);

    const buyType = normalizeVolumeDiscountDiscountType(offer?.buyDiscountType);
    const getType = normalizeVolumeDiscountDiscountType(offer?.getDiscountType);
    const buyVal = Math.max(0, Number(offer?.buyDiscountValue) || 0);
    const getVal = Math.max(0, Number(offer?.getDiscountValue) || 0);

    let buyTotal = price * buyQty;
    if (buyType === VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE) {
      buyTotal -= (buyTotal * Math.min(100, buyVal)) / 100;
    } else if (buyType === VOLUME_DISCOUNT_DISCOUNT_TYPE.FIXED_AMOUNT) {
      // Fixed amount = off per buy item
      const perItem = Math.min(buyVal, price);
      buyTotal = Math.max(0, buyTotal - perItem * buyQty);
    }

    let getTotal = price * getQty;
    if (getType === VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE) {
      getTotal -= (getTotal * Math.min(100, getVal)) / 100;
    } else if (getType === VOLUME_DISCOUNT_DISCOUNT_TYPE.FIXED_AMOUNT) {
      // Fixed amount = off per get item
      const perItem = Math.min(getVal, price);
      getTotal = Math.max(0, getTotal - perItem * getQty);
    }

    final = Math.max(0, buyTotal + getTotal);
  } else {
    const qty = Math.max(1, Number(offer?.quantity) || 1);
    original = price * qty;
    final = original;
    const type = normalizeVolumeDiscountDiscountType(offer?.discountType);
    const val = Math.max(0, Number(offer?.discountValue) || 0);
    if (type === VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE) {
      final = original - (original * Math.min(100, val)) / 100;
    } else if (type === VOLUME_DISCOUNT_DISCOUNT_TYPE.FIXED_AMOUNT) {
      // Fixed amount = off per item in the pack
      const perItem = Math.min(val, price);
      final = Math.max(0, original - perItem * qty);
    }
  }

  const saved = Math.max(0, original - final);
  const savedPercentage =
    original > 0 ? Math.round((saved / original) * 100) : 0;

  return {
    original: Number(original.toFixed(2)),
    final: Number(final.toFixed(2)),
    saved: Number(saved.toFixed(2)),
    savedPercentage,
  };
}

/**
 * Replace `{saved_percentage}`, `{saved_total}`, `{{timer}}` placeholders.
 * Case-insensitive. Legacy `{saved_percentage} off` (no %) → `15% off`.
 * Does not force `%` onto `{saved_total}` amounts.
 */
export function applyVolumeDiscountPlaceholders(text, vars = {}) {
  if (text == null || text === "") return "";
  let out = String(text);
  const usedPercentagePlaceholder = /\{saved_percentage\}/i.test(out);
  if (vars.savedPercentage != null) {
    out = out.replace(/\{saved_percentage\}/gi, String(vars.savedPercentage));
  }
  if (vars.savedTotal != null) {
    out = out.replace(/\{saved_total\}/gi, String(vars.savedTotal));
  }
  if (vars.timer != null) {
    out = out.replace(/\{\{timer\}\}/gi, String(vars.timer));
  }
  if (usedPercentagePlaceholder) {
    out = out.replace(/(\d+(?:\.\d+)?)(?!\s*%)(\s+)off\b/gi, "$1%$2off");
  }
  return out;
}

/** Default badge text for a discount type (auto-filled in the editor). */
export function defaultVolumeDiscountLabelForType(discountType) {
  const type = normalizeVolumeDiscountDiscountType(discountType);
  if (type === VOLUME_DISCOUNT_DISCOUNT_TYPE.PERCENTAGE) {
    return "{saved_percentage}% off";
  }
  if (type === VOLUME_DISCOUNT_DISCOUNT_TYPE.FIXED_AMOUNT) {
    return "{saved_total} off";
  }
  return "";
}

/** True when label is empty or still a stock template we can safely replace. */
export function isDefaultVolumeDiscountLabel(label) {
  const t = String(label || "").trim().toLowerCase();
  if (!t) return true;
  return (
    t === "{saved_percentage}% off" ||
    t === "{saved_percentage} off" ||
    t === "{saved_total} off" ||
    t === "{saved_total}% off"
  );
}

/** Shopify Function offerRuleId for paid BXGY units. */
export function volumeDiscountBuyOfferRuleId(offerId) {
  if (offerId == null || String(offerId) === "") return "";
  return `${String(offerId)}:buy`;
}

/** Shopify Function offerRuleId for free/get BXGY units. */
export function volumeDiscountGetOfferRuleId(offerId) {
  if (offerId == null || String(offerId) === "") return "";
  return `${String(offerId)}:get`;
}

/**
 * Human-readable discount title for metafield / checkout (placeholders resolved).
 */
export function resolveVolumeDiscountOfferDiscountTitle(offer, offerMode) {
  const mode = normalizeVolumeDiscountOfferMode(offerMode);
  const pricing = calculateVolumeDiscountOfferPrice(offer, 100, mode);
  const raw =
    (offer?.discountLabel && String(offer.discountLabel).trim()) ||
    (offer?.title && String(offer.title).trim()) ||
    "Volume Discount";
  const resolved = applyVolumeDiscountPlaceholders(raw, {
    savedPercentage: pricing.savedPercentage,
    savedTotal: String(pricing.saved),
  });
  // Drop unresolved placeholders rather than showing them at checkout.
  return resolved
    .replace(/\{saved_percentage\}/gi, String(pricing.savedPercentage))
    .replace(/\{saved_total\}/gi, String(pricing.saved))
    .trim() || `${pricing.savedPercentage}% off`;
}

export function getVolumeDiscountLayout(popupOrStep) {
  const fromStep =
    popupOrStep?.volumeDiscountLayout ||
    popupOrStep?.schema?.[0]?.volumeDiscountLayout;
  const fromGs = popupOrStep?.generalSettings?.volumeDiscountLayout;
  return fromStep || fromGs || null;
}

export function buildDefaultVolumeDiscountSchema(offerMode) {
  const layout = buildDefaultVolumeDiscountLayout(offerMode);
  const sections = buildVolumeDiscountSections(layout);
  return {
    popUpType: VOLUME_DISCOUNT_TEMPLATE_TYPE,
    generalSettings: {
      title: "Volume Discount",
      isStaticVolumeDiscount: true,
      isDraggable: false,
      lockDraggable: true,
      popupLayoutId: VOLUME_DISCOUNT_LAYOUT_ID,
      volumeDiscountLayout: layout,
      condition: {
        product: {
          allowTrigger: true,
          oneTimeShow: false,
          offer_is_triggered: {
            type: "all_product",
            value: true,
          },
          // Volume discount uses the PDP product — no catalog offer picker.
          products_to_offer: {
            type: "show_current_product",
            value: [],
          },
          allowMultipleProducts: false,
          products_to_offer_limit: 1,
        },
        embed: {
          is_embedded: true,
          slot_mode: true,
          slot_no: VOLUME_DISCOUNT_SLOT_NO,
          step: 0,
        },
        ecommerce: {
          enabled: false,
          rules: [],
        },
      },
      hasProduct: true,
    },
    schema: [
      {
        volumeDiscountLayout: layout,
        section: sections,
      },
    ],
  };
}

export function buildVolumeDiscountTemplateDocument(offerMode) {
  const default_schema = buildDefaultVolumeDiscountSchema(offerMode);
  return {
    title: "Volume Discount",
    description:
      "Product-page volume discount widget with Quantity breaks or Buy X Get Y layouts. Shows on selected products via the WeUpsell Volume Discount theme block.",
    template_type: VOLUME_DISCOUNT_TEMPLATE_TYPE,
    active: true,
    search_tags: [
      "volume_discount",
      "quantity_breaks",
      "buy_x_get_y",
      "product_page",
      "pre_purchase",
      "embedded",
      "beta",
    ],
    index_order_number: 3.2,
    platform_supported: ["shopify"],
    category: {
      primary: "Upsell & Cross sell",
      key: "upsell-crosssell",
    },
    default_schema,
  };
}
